import { z } from 'zod';
import { roleSchema } from './commun';

/** Longueur minimale d'un mot de passe local (SPEC 11). */
export const MDP_MIN = 12;

/** Réponse de `GET /api/moi` : l'utilisateur connecté. */
export const moiSchema = z.object({
  utilisateurId: z.string(),
  nom: z.string(),
  courriel: z.string(),
  role: roleSchema,
  personneCle: z.string().nullable(),
  modesAuthentification: z.array(z.string()),
});
export type Moi = z.infer<typeof moiSchema>;

/** Réponse de `GET /api/auth/options` (anonyme) : méthodes de connexion proposées sur /connexion. */
export const optionsConnexionSchema = z.object({
  /** Formulaire courriel et mot de passe affiché. */
  local: z.boolean(),
  /** Bouton Microsoft affiché. */
  entra: z.boolean(),
  /** Connexion locale réservée aux administrateurs (mode entra avec secours). */
  secoursAdminSeulement: z.boolean(),
});
export type OptionsConnexion = z.infer<typeof optionsConnexionSchema>;

export const connexionLocaleSchema = z.object({
  email: z.email(),
  password: z.string().min(1),
});

/** Motif de refus de connexion lu dans `?erreur=` de l'URL de /connexion. */
export const MOTIFS_ERREUR_CONNEXION = ['en_attente', 'banni', 'refuse', 'echec'] as const;

export const utilisateurSchema = z.object({
  id: z.string(),
  nom: z.string(),
  courriel: z.string(),
  role: roleSchema,
  personneCle: z.string().nullable(),
  personneNom: z.string().nullable(),
  /** Faux si le compte est bloqué ou en attente d'activation. */
  actif: z.boolean(),
  /** Compte Entra créé à la première connexion, sans rôle confirmé ni personne liée. */
  enAttente: z.boolean(),
  /** Compte relié à Microsoft Entra. */
  entra: z.boolean(),
  creeLe: z.string(),
  /** Ouverture de session la plus récente (journal des connexions, ou sessions encore en base). */
  derniereConnexion: z.string().nullable(),
  /** Dernier usage connu d'une session, mis à jour au plus une fois par jour. */
  derniereActivite: z.string().nullable(),
  /** Connexions des 30 derniers jours (journal des connexions). */
  connexions30j: z.number().int(),
});
export type Utilisateur = z.infer<typeof utilisateurSchema>;

export const personneChoixSchema = z.object({ cle: z.string(), nom: z.string() });

export const listeUtilisateursSchema = z.object({
  utilisateurs: z.array(utilisateurSchema),
  personnes: z.array(personneChoixSchema),
});
export type ListeUtilisateurs = z.infer<typeof listeUtilisateursSchema>;

export const creerUtilisateurSchema = z.object({
  courriel: z.email(),
  nom: z.string().trim().min(1).max(120),
  role: roleSchema,
  personneCle: z.string().trim().min(1).nullable().default(null),
  motDePasse: z.string().min(MDP_MIN, `Au moins ${MDP_MIN} caractères`).max(256),
});
export type CreerUtilisateur = z.input<typeof creerUtilisateurSchema>;

export const modifierUtilisateurSchema = z
  .object({
    nom: z.string().trim().min(1).max(120),
    role: roleSchema,
    personneCle: z.string().trim().min(1).nullable(),
    actif: z.boolean(),
    motDePasse: z.string().min(MDP_MIN, `Au moins ${MDP_MIN} caractères`).max(256),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Aucune modification' });
export type ModifierUtilisateur = z.infer<typeof modifierUtilisateurSchema>;

export const LIBELLES_ROLE: Record<z.infer<typeof roleSchema>, string> = {
  admin: 'Administrateur',
  backoffice: 'Back-office',
  mentor: 'Mentor',
  terrain: 'Terrain',
};
